import { 
  getFirestore, doc, getDoc, updateDoc, deleteDoc, 
  collection, getDocs, query, where, increment, addDoc ,writeBatch
} from "firebase/firestore";

const db = getFirestore();



class DailyServings {
  constructor(items, totalCost,realCost, date, restaurantId) {
    this.items = items; // Array of { productId, productName, quantity, costPrice }
    this.totalCost = totalCost;
    this.realCost = realCost;
    this.date = date;
    this.restaurantId = restaurantId;
    this.id = null;

  }

  async addDailyServing() {
    try {
      // First pass: Verify all stocks are available
      for (const item of this.items) {
        const productDoc = await getDoc(doc(db, "products", item.productId));
        if (!productDoc.exists()) {
          throw new Error(`Product ${item.productId} not found`);
        }

        const ingredients = productDoc.data().ingredients;
        for (const ingredient of ingredients) {
          const stockQuery = query(
            collection(db, "stocks"),
            where("productName", "==", ingredient.stockName),
            where("restaurantId", "==", this.restaurantId),

          );

          const stockSnap = await getDocs(stockQuery);
          if (stockSnap.empty) {
            throw new Error(`Stock not found for ${ingredient.stockName} (${ingredient.unit})`);
          }

          const stockDoc = stockSnap.docs[0];
          const requiredQty = ingredient.quantity * item.quantity;
          
          if (stockDoc.data().quantity < requiredQty) {
            throw new Error(
              `Insufficient ${ingredient.stockName}. ` +
              `Required: ${requiredQty} ${ingredient.unit}, ` +
              `Available: ${stockDoc.data().quantity} ${ingredient.unit}`
            );
          }
        }
      }

      // Second pass: Deduct stocks
      const stockUpdates = [];
      for (const item of this.items) {
        const productDoc = await getDoc(doc(db, "products", item.productId));
        const ingredients = productDoc.data().ingredients;

        for (const ingredient of ingredients) {
          const stockQuery = query(
            collection(db, "stocks"),
            where("productName", "==", ingredient.stockName),
            where("restaurantId", "==", this.restaurantId),
           
          );

          const stockSnap = await getDocs(stockQuery);
          const stockDoc = stockSnap.docs[0];
          const stockRef = doc(db, "stocks", stockDoc.id);
          const requiredQty = ingredient.quantity * item.quantity;

          stockUpdates.push(
            updateDoc(stockRef, {
              quantity: increment(-requiredQty)
            })
          );
        }
      }

      await Promise.all(stockUpdates);

      // Create daily serving document
      const servingData = {
        items: this.items,
        totalCost: this.totalCost,
        realCost: this.realCost,
        date: this.date,
        restaurantId: this.restaurantId
      };

      const servingRef = await addDoc(collection(db, "daily_servings"), servingData);
      this.id = servingRef.id;
      return servingRef;

    } catch (error) {
      console.error("Error adding daily serving:", error);
      throw error;
    }
  }


  static async updateDailyServing(servingId, updates) {
    const db = getFirestore();
    try {
      const servingRef = doc(db, "daily_servings", servingId);
      const servingDoc = await getDoc(servingRef);
      
      if (!servingDoc.exists()) {
        throw new Error("Daily serving not found");
      }
  
      const originalServing = servingDoc.data();
      const originalItems = originalServing.items || [];
      const updatedItems = updates.items || [];
  
      // 1. Calculate quantity differences
      const quantityDeltas = new Map();
      
      // Find modified items
      updatedItems.forEach(updatedItem => {
        const originalItem = originalItems.find(i => i.productId === updatedItem.productId);
        if (originalItem) {
          const delta = updatedItem.quantity - originalItem.quantity;
          if (delta !== 0) {
            quantityDeltas.set(updatedItem.productId, {
              delta,
              ingredients: [] // Will be populated later
            });
          }
        }
      });
  
      // 2. Verify stock for positive deltas (increased quantities)
      if (quantityDeltas.size > 0) {
        // Load product ingredients for changed items
        await Promise.all(Array.from(quantityDeltas.keys()).map(async productId => {
          const productDoc = await getDoc(doc(db, "products", productId));
          if (!productDoc.exists()) {
            throw new Error(`Product ${productId} not found`);
          }
          quantityDeltas.get(productId).ingredients = productDoc.data().ingredients || [];
        }));
  
        // Verify stock availability for increased quantities
        for (const [productId, { delta, ingredients }] of quantityDeltas) {
          if (delta > 0) {
            for (const ingredient of ingredients) {
              const stockQuery = query(
                collection(db, "stocks"),
                where("productName", "==", ingredient.stockName),
                where("restaurantId", "==", updates.restaurantId)
              );
  
              const stockSnap = await getDocs(stockQuery);
              if (stockSnap.empty) {
                throw new Error(`Stock not found for ${ingredient.stockName}`);
              }
  
              const stockDoc = stockSnap.docs[0];
              const requiredDelta = ingredient.quantity * delta;
              
              if (stockDoc.data().quantity < requiredDelta) {
                throw new Error(
                  `Insufficient ${ingredient.stockName} for update. ` +
                  `Required: +${requiredDelta} ${ingredient.unit}, ` +
                  `Available: ${stockDoc.data().quantity} ${ingredient.unit}`
                );
              }
            }
          }
        }
      }
  
      // 3. Update stocks based on quantity differences
      const stockUpdates = [];
      for (const [productId, { delta, ingredients }] of quantityDeltas) {
        for (const ingredient of ingredients) {
          const stockQuery = query(
            collection(db, "stocks"),
            where("productName", "==", ingredient.stockName),
            where("restaurantId", "==", updates.restaurantId)
          );
  
          const stockSnap = await getDocs(stockQuery);
          const stockDoc = stockSnap.docs[0];
          const stockRef = doc(db, "stocks", stockDoc.id);
          
          const quantityChange = ingredient.quantity * delta;
          stockUpdates.push({
            stockRef,
            quantityChange
          });
        }
      }
  
      // 4. Perform all updates in a transaction
      const batch = writeBatch(db);
      
      // Update stocks
      stockUpdates.forEach(update => {
        const { stockRef, quantityChange } = update;
        batch.update(stockRef, {
          quantity: increment(-quantityChange) // Negative delta = add back stock
        });
      });
  
      // Update serving document
      batch.update(servingRef, {
        items: updates.items.map(item => ({
          productId: item.productId,
          productName: item.productName,
          quantity: item.quantity,
          costPrice: item.costPrice,
          soldPrice: item.soldPrice
        })),
        totalCost: updates.totalCost,
        realCost: updates.realCost,
        date: updates.date
      });
  
      await batch.commit();
  
      return servingRef;
  
    } catch (error) {
      console.error("Error updating daily serving:", error);
      throw new Error(`Failed to update daily serving: ${error.message}`);
    }
  }
  
  // Get all daily servings
  static async getAllDailyServings() {
    try {
      const servingsSnapshot = await getDocs(collection(db, "daily_servings"));
      const servings = [];
      servingsSnapshot.forEach((doc) => {
        servings.push({ id: doc.id, ...doc.data() });
      });
      return servings;
    } catch (error) {
      console.error("Error retrieving daily servings:", error.message);
      throw new Error(error.message);
    }
  }

  // Get daily servings by restaurant ID
  static async getDailyServingsByRestaurantId(restaurantId) {
    try {
      const servingsQuery = query(
        collection(db, "daily_servings"),
        where("restaurantId", "==", restaurantId)
      );
      const servingsSnapshot = await getDocs(servingsQuery);
      const servings = [];
      servingsSnapshot.forEach((doc) => {
        servings.push({ id: doc.id, ...doc.data() });
      });
      return servings;
    } catch (error) {
      console.error("Error retrieving daily servings by restaurant ID:", error.message);
      throw new Error(error.message);
    }
  }

// Delete a daily serving and restore stock
static async deleteDailyServing(servingId) {
  try {
    const servingRef = doc(db, "daily_servings", servingId);
    const servingSnap = await getDoc(servingRef);

    if (!servingSnap.exists()) {
      throw new Error("Daily serving not found");
    }

    const servingData = servingSnap.data();
    const { items, restaurantId } = servingData;

    // Restore stock quantities
    const stockRestorePromises = items.map(async (item) => {
      try {
        const productDoc = await getDoc(doc(db, "products", item.productId));
        if (!productDoc.exists()) {
          console.warn(`Product ${item.productId} not found - skipping stock restoration`);
          return;
        }

        const ingredients = productDoc.data().ingredients;
        return ingredients.map(async (ingredient) => {
          const stockQuery = query(
            collection(db, "stocks"),
            where("productName", "==", ingredient.stockName),
            where("restaurantId", "==", restaurantId),
      
          );

          const stockSnap = await getDocs(stockQuery);
          if (stockSnap.empty) {
            console.warn(`Stock ${ingredient.stockName} (${ingredient.unit}) not found - skipping restoration`);
            return;
          }

          const stockDoc = stockSnap.docs[0];
          const stockRef = doc(db, "stocks", stockDoc.id);
          const restoredQty = ingredient.quantity * item.quantity;

          await updateDoc(stockRef, {
            quantity: increment(restoredQty)
          });
        });
      } catch (error) {
        console.error(`Error restoring stock for product ${item.productId}:`, error);
      }
    });

    // Wait for all stock restorations to complete
    await Promise.all(stockRestorePromises.flat());
    
    // Finally delete the serving
    await deleteDoc(servingRef);
    console.log("Daily serving deleted and stock restored successfully!");
  } catch (error) {
    console.error("Error deleting daily serving:", error.message);
    throw error;
  }
}
}

export default DailyServings;